"use client";

import {
	FlaskConical,
	Truck,
	HelpCircle,
	MessageSquare,
	ArrowRight,
	Sparkles,
	Search,
	BookOpen,
	Layers,
	Calculator,
	Activity,
	Droplets,
	Scale,
	Sprout,
	HeartPulse,
	Sun,
	Hourglass,
	Brain,
	GitCompareArrows,
} from "lucide-react";
import { MegaFeatureCard } from "./mega-feature-card";

type IconComponent = React.ComponentType<{ className?: string }>;

const CATEGORY_ICONS: Record<string, IconComponent> = {
	"weight-management": Scale,
	"growth-hormone": Sprout,
	recovery: HeartPulse,
	skin: Sun,
	"anti-aging": Hourglass,
	cognition: Brain,
	"bac-water": Droplets,
};

export function getCategoryIcon(slug: string): IconComponent {
	return CATEGORY_ICONS[slug] ?? FlaskConical;
}
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { cn } from "@/lib/utils";

// ---------- Types ----------

interface CategoryItem {
	id: string;
	name: string;
	slug: string;
	description?: string | null;
}

interface FeaturedProduct {
	name: string;
	slug: string;
	imageUrl: string | null;
	pricePresentation: ResolvedMoneyPresentation | null;
	locale: string;
	channel: string;
}

interface PeptidesPanelLabels {
	categories: string;
	popular: string;
	featured: string;
	browseAll: string;
	allPeptides: string;
	viewProduct: string;
	quiz: string;
	quizDesc: string;
	guide: string;
	guideDesc: string;
	stackBuilder?: string;
	stackBuilderDesc?: string;
	calculator?: string;
	calculatorDesc?: string;
	halfLifeCalculator?: string;
	halfLifeCalculatorDesc?: string;
	compare?: string;
	compareDesc?: string;
	quizCta?: string;
	glowStackEyebrow: string;
	glowStackTitle: string;
	glowStackDesc: string;
}

interface ResearchPanelLabels {
	tools: string;
	guide: string;
	guideDesc: string;
	calculator: string;
	calculatorDesc: string;
	halfLifeCalculator?: string;
	halfLifeCalculatorDesc?: string;
	guides: string;
	guidesDesc: string;
	documentation: string;
	focusProduct: string;
	retatrutideEyebrow: string;
	retatrutideHighlight: string;
	retatrutideHighlightDesc: string;
	partners: string;
	partnersDesc: string;
	newsletter: string;
	newsletterDesc: string;
	viewProduct: string;
}

interface SupportPanelLabels {
	shippingOrders: string;
	shipping: string;
	help: string;
	faq: string;
	contact: string;
}

// ---------- Shared ----------

function ColumnHeading({ children }: { children: React.ReactNode }) {
	return (
		<h3 className="after:bg-primary/25 relative mb-4 pb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground after:absolute after:bottom-0 after:left-0 after:h-px after:w-8">
			{children}
		</h3>
	);
}

function MenuLink({
	href,
	icon: Icon,
	label,
	description,
	className,
}: {
	href: string;
	icon: React.ComponentType<{ className?: string }>;
	label: string;
	description?: string;
	className?: string;
}) {
	return (
		<LinkWithChannel
			href={href}
			className={cn(
				"group flex items-start gap-3 rounded-xl p-2.5 transition-all duration-150 hover:bg-muted",
				className,
			)}
		>
			<div className="bg-primary/10 group-hover:bg-primary/15 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors">
				<Icon className="h-4 w-4 text-primary" />
			</div>
			<div className="min-w-0 pt-0.5">
				<span className="text-[13px] font-medium text-foreground">{label}</span>
				{description && (
					<p className="mt-0.5 line-clamp-2 text-[12px] leading-snug text-muted-foreground">{description}</p>
				)}
			</div>
		</LinkWithChannel>
	);
}

// ---------- Peptides Panel ----------

export function PeptidesPanel({
	categories,
	featured,
	labels,
}: {
	categories: CategoryItem[];
	featured: FeaturedProduct | null;
	labels: PeptidesPanelLabels;
}) {
	return (
		<div className="grid grid-cols-[1.05fr_1.05fr_0.95fr] gap-7">
			{/* Column 1: Categories */}
			<div>
				<ColumnHeading>{labels.categories}</ColumnHeading>
				<div className="space-y-0.5">
					{categories.map((cat) => {
						const Icon = getCategoryIcon(cat.slug);
						return (
							<LinkWithChannel
								key={cat.id}
								href={`/categories/${cat.slug}`}
								className="hover:bg-primary/5 group flex items-start gap-2.5 rounded-lg px-2 py-2 transition-all duration-150"
							>
								<span className="bg-primary/10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md">
									<Icon className="h-3 w-3 text-primary" />
								</span>
								<div className="min-w-0">
									<span className="text-[13px] font-medium text-foreground">{cat.name}</span>
									{cat.description && (
										<p className="mt-0.5 line-clamp-1 text-[11px] leading-snug text-muted-foreground">
											{cat.description}
										</p>
									)}
								</div>
							</LinkWithChannel>
						);
					})}
					<LinkWithChannel
						href="/products/bac-water-10ml"
						className="hover:bg-primary/5 group flex items-start gap-2.5 rounded-lg px-2 py-2 transition-all duration-150"
					>
						<span className="bg-primary/10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md">
							<Droplets className="h-3 w-3 text-primary" />
						</span>
						<div className="min-w-0">
							<span className="text-[13px] font-medium text-foreground">BAC Water</span>
						</div>
					</LinkWithChannel>
					<LinkWithChannel
						href="/products"
						className="hover:bg-primary/5 group mt-2 flex items-center gap-1.5 rounded-lg px-2 py-2 text-[13px] font-medium text-primary transition-colors"
					>
						{labels.browseAll}
						<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
					</LinkWithChannel>
				</div>
			</div>

			{/* Column 2: Quiz Hero + Quick Links */}
			<div>
				<ColumnHeading>{labels.popular}</ColumnHeading>
				<div className="space-y-1.5">
					{/* Quiz Hero Card */}
					<LinkWithChannel
						href="/quiz"
						className="from-primary/10 via-primary/5 group relative block overflow-hidden rounded-xl bg-gradient-to-br to-transparent p-4 transition-all duration-200 hover:shadow-md"
					>
						<div className="flex items-start gap-3">
							<div className="bg-primary/15 group-hover:bg-primary/20 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors">
								<Sparkles className="h-5 w-5 text-primary transition-transform duration-300 group-hover:scale-110" />
							</div>
							<div className="min-w-0 flex-1">
								<span className="text-sm font-semibold text-foreground">{labels.quiz}</span>
								<p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{labels.quizDesc}</p>
								<span className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-primary">
									{labels.quizCta ?? "Start quiz"}
									<ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
								</span>
							</div>
						</div>
					</LinkWithChannel>

					{/* Standard links */}
					{labels.stackBuilder && (
						<MenuLink
							href="/stack-builder"
							icon={Layers}
							label={labels.stackBuilder}
							description={labels.stackBuilderDesc ?? ""}
						/>
					)}
					{labels.calculator && (
						<MenuLink
							href="/peptide-calculator"
							icon={Calculator}
							label={labels.calculator}
							description={labels.calculatorDesc ?? ""}
						/>
					)}
					{labels.halfLifeCalculator && (
						<MenuLink
							href="/half-life-calculator"
							icon={Activity}
							label={labels.halfLifeCalculator}
							description={labels.halfLifeCalculatorDesc ?? ""}
						/>
					)}
					{labels.compare && (
						<MenuLink
							href="/peptides/compare"
							icon={GitCompareArrows}
							label={labels.compare}
							description={labels.compareDesc ?? ""}
						/>
					)}
				</div>
			</div>

			{/* Column 3: Featured Product */}
			<div>
				{featured ? (
					<MegaFeatureCard
						eyebrow={labels.glowStackEyebrow}
						title={labels.glowStackTitle}
						description={labels.glowStackDesc}
						cta={labels.viewProduct}
						href={`/products/${featured.slug}`}
					/>
				) : null}
			</div>
		</div>
	);
}

// ---------- Research Panel ----------

export function ResearchPanel({
	labels,
	focusProduct,
}: {
	labels: ResearchPanelLabels;
	focusProduct: FeaturedProduct | null;
}) {
	return (
		<div className="grid grid-cols-[1.05fr_1.05fr_0.95fr] gap-7">
			{/* Column 1: Tools */}
			<div>
				<ColumnHeading>{labels.tools}</ColumnHeading>
				<div className="space-y-0.5">
					<MenuLink href="/peptides" icon={Search} label={labels.guide} description={labels.guideDesc} />
					<MenuLink
						href="/peptide-calculator"
						icon={Calculator}
						label={labels.calculator}
						description={labels.calculatorDesc}
					/>
					{labels.halfLifeCalculator && (
						<MenuLink
							href="/half-life-calculator"
							icon={Activity}
							label={labels.halfLifeCalculator}
							description={labels.halfLifeCalculatorDesc ?? ""}
						/>
					)}
				</div>
			</div>

			{/* Column 2: Documentation */}
			<div>
				<ColumnHeading>{labels.documentation}</ColumnHeading>
				<div className="space-y-0.5">
					<MenuLink href="/guides" icon={BookOpen} label={labels.guides} description={labels.guidesDesc} />
				</div>
			</div>

			{/* Column 3: Focus product */}
			<div>
				{focusProduct ? (
					<MegaFeatureCard
						eyebrow={labels.retatrutideEyebrow}
						title={labels.retatrutideHighlight}
						description={labels.retatrutideHighlightDesc}
						cta={labels.viewProduct}
						href={`/products/${focusProduct.slug}`}
					/>
				) : null}
			</div>
		</div>
	);
}

// ---------- Support Panel ----------

export function SupportPanel({ labels }: { labels: SupportPanelLabels }) {
	return (
		<div className="grid grid-cols-2 gap-10">
			{/* Column 1: Shipping & Orders */}
			<div>
				<ColumnHeading>{labels.shippingOrders}</ColumnHeading>
				<div className="space-y-0.5">
					<MenuLink href="/shipping" icon={Truck} label={labels.shipping} />
				</div>
			</div>

			{/* Column 2: Help */}
			<div>
				<ColumnHeading>{labels.help}</ColumnHeading>
				<div className="space-y-0.5">
					<MenuLink href="/faq" icon={HelpCircle} label={labels.faq} />
					<MenuLink href="/contact" icon={MessageSquare} label={labels.contact} />
				</div>
			</div>
		</div>
	);
}

// Re-export types for use in header
export type { CategoryItem, FeaturedProduct, PeptidesPanelLabels, ResearchPanelLabels, SupportPanelLabels };
